import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js';
import { createApiClient, type ApiClient } from '@/lib/api.js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  accessToken: string | null;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  api: ApiClient;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export function AuthProvider({ children }: AuthProviderProps) {
  const [supabase] = useState<SupabaseClient>(() => {
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('Missing Supabase environment variables');
      return null as any;
    }
    return createClient(supabaseUrl, supabaseAnonKey);
  });

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Initialize auth state
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      setSession(data.session);
      setUser(data.user);
      setAccessToken(data.session?.access_token ?? null);
    },
    [supabase]
  );

  const signUp = useCallback(
    async (email: string, password: string) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      setSession(data.session);
      setUser(data.user);
      setAccessToken(data.session?.access_token ?? null);
    },
    [supabase]
  );

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      throw error;
    }
  }, [supabase]);

  const signOut = useCallback(async () => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { error } = await supabase.auth.signOut();

    if (error) {
      throw error;
    }

    setSession(null);
    setUser(null);
    setAccessToken(null);
  }, [supabase]);

  // Authenticated fetch wrapper
  const authenticatedFetch = useCallback(
    async (url: string, options?: RequestInit): Promise<Response> => {
      const headers = new Headers(options?.headers);

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      // Add X-End-User-Id header for multi-tenant isolation
      if (user?.id) {
        headers.set('X-End-User-Id', user.id);
      }

      if (!headers.has('Content-Type') && options?.body && typeof options.body === 'string') {
        headers.set('Content-Type', 'application/json');
      }

      return fetch(url, {
        ...options,
        headers,
      });
    },
    [accessToken, user?.id]
  );

  // API client with authenticated fetch
  const api = useMemo(() => createApiClient(authenticatedFetch), [authenticatedFetch]);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: !!user,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      accessToken,
      authenticatedFetch,
      api,
    }),
    [user, session, loading, signIn, signUp, signInWithGoogle, signOut, accessToken, authenticatedFetch, api]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
